/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { NewPerson } from '../models/new-person';
import { Person } from '../models/person';
import { PersonPartial } from '../models/person-partial';
import { PersonWithRelations } from '../models/person-with-relations';
import { ProjectRole } from '../models/project-role';
import { Count as LoopbackCount } from '../models/loopback/count';

@Injectable({
  providedIn: 'root',
})
export class PersonControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation personControllerCountByAdmin
   */
  static readonly PersonControllerCountByAdminPath = '/admin/people/count';

  /**
   * Getting person count with condition (where) if provided. If condition does not be provided then this endpoint returns all person count.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `countByAdmin()` instead.
   *
   * This method doesn't expect any request body.
   */
  countByAdmin$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerCountByAdminPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }

  /**
   * Getting person count with condition (where) if provided. If condition does not be provided then this endpoint returns all person count.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `countByAdmin$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  countByAdmin(params?: {
    where?: any;
  }): Observable<LoopbackCount> {

    return this.countByAdmin$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation personControllerFindByAdmin
   */
  static readonly PersonControllerFindByAdminPath = '/admin/people';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findByAdmin()` instead.
   *
   * This method doesn't expect any request body.
   */
  findByAdmin$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<Person>>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerFindByAdminPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Person>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findByAdmin$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findByAdmin(params?: {
    filter?: any;
  }): Observable<Array<Person>> {

    return this.findByAdmin$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Person>>) => r.body as Array<Person>)
    );
  }

  /**
   * Path part for operation personControllerCount
   */
  static readonly PersonControllerCountPath = '/people/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<LoopbackCount> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation personControllerGetProfilePicture
   */
  static readonly PersonControllerGetProfilePicturePath = '/people/profile-picture/{personId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProfilePicture()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProfilePicture$Response(params: {
    personId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerGetProfilePicturePath, 'get');
    if (params) {
      rb.path('personId', params.personId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getProfilePicture$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProfilePicture(params: {
    personId: string;
  }): Observable<void> {

    return this.getProfilePicture$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation personControllerGetProjectRoles
   */
  static readonly PersonControllerGetProjectRolesPath = '/people/roles';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProjectRoles()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProjectRoles$Response(params?: {
  }): Observable<StrictHttpResponse<Array<ProjectRole>>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerGetProjectRolesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ProjectRole>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getProjectRoles$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProjectRoles(params?: {
  }): Observable<Array<ProjectRole>> {

    return this.getProjectRoles$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ProjectRole>>) => r.body as Array<ProjectRole>)
    );
  }

  /**
   * Path part for operation personControllerUploadProfilePicture
   */
  static readonly PersonControllerUploadProfilePicturePath = '/people/upload-profile-picture';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadProfilePicture()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadProfilePicture$Response(params: {

    /**
     * multipart/form-data value.
     */
    body: {  }
  }): Observable<StrictHttpResponse<Person>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerUploadProfilePicturePath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Person>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `uploadProfilePicture$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadProfilePicture(params: {

    /**
     * multipart/form-data value.
     */
    body: {  }
  }): Observable<Person> {

    return this.uploadProfilePicture$Response(params).pipe(
      map((r: StrictHttpResponse<Person>) => r.body as Person)
    );
  }

  /**
   * Path part for operation personControllerVerify
   */
  static readonly PersonControllerVerifyPath = '/people/verify/{token}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `verify()` instead.
   *
   * This method doesn't expect any request body.
   */
  verify$Response(params: {
    token: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerVerifyPath, 'patch');
    if (params) {
      rb.path('token', params.token, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `verify$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  verify(params: {
    token: string;
  }): Observable<void> {

    return this.verify$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation personControllerFindById
   */
  static readonly PersonControllerFindByIdPath = '/people/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<PersonWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PersonWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<PersonWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<PersonWithRelations>) => r.body as PersonWithRelations)
    );
  }

  /**
   * Path part for operation personControllerSoftDeleteById
   */
  static readonly PersonControllerSoftDeleteByIdPath = '/people/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerSoftDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `softDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.softDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation personControllerUpdateById
   */
  static readonly PersonControllerUpdateByIdPath = '/people/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: PersonPartial
  }): Observable<StrictHttpResponse<Person>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Person>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: PersonPartial
  }): Observable<Person> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<Person>) => r.body as Person)
    );
  }

  /**
   * Path part for operation personControllerFind
   */
  static readonly PersonControllerFindPath = '/people';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<Person>>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Person>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<Person>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Person>>) => r.body as Array<Person>)
    );
  }

  /**
   * Path part for operation personControllerCreate
   */
  static readonly PersonControllerCreatePath = '/people';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewPerson
  }): Observable<StrictHttpResponse<Person>> {

    const rb = new RequestBuilder(this.rootUrl, PersonControllerService.PersonControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Person>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewPerson
  }): Observable<Person> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Person>) => r.body as Person)
    );
  }

}
